import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  Checkbox,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { SourceType, ContentType, DestinationSelection } from './TransferManager';
import { moodleApi, MoodleSource, MoodleCourse } from '../services/moodle-api';
import { basePrefixApi, environmentApi, contentApi } from '../services/api';
import { CMI5ActivitiesRow } from './CMI5ActivitiesRow';

interface DestinationPaneProps {
  currentEnvironment: string;
  sourceContentType?: ContentType;
  sourceItems?: any[]; // All items from source
  selectedSourceItems?: string[]; // IDs/keys of selected items from source
  onSelectionChange: (selection: DestinationSelection | null) => void;
  onItemsChange?: (items: any[]) => void; // Expose all loaded items
}

export const DestinationPane: React.FC<DestinationPaneProps> = ({
  currentEnvironment,
  sourceContentType,
  sourceItems = [],
  selectedSourceItems = [],
  onSelectionChange,
  onItemsChange,
}) => {
  const [destinationType, setDestinationType] = useState<SourceType>('s3');
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    shortname: true,
    category: true,
    size: true,
    date: true,
  });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Moodle-specific state
  const [moodleSources, setMoodleSources] = useState<MoodleSource[]>([]);
  const [selectedMoodleSource, setSelectedMoodleSource] = useState<string>('');
  const [moodleCategories, setMoodleCategories] = useState<Array<{ id: number; name: string; parent: number; path: string }>>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(1); // Default to category 1
  const [moodleItems, setMoodleItems] = useState<any[]>([]);

  // S3-specific state
  const [s3Environments, setS3Environments] = useState<Array<{ id: string; name: string; prefix: string; bucketName: string }>>([]);
  const [selectedS3Environment, setSelectedS3Environment] = useState<string>('');
  const [s3Items, setS3Items] = useState<any[]>([]);

  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customNames, setCustomNames] = useState<Record<string, string>>({}); // Store custom names for duplicate items
  const [expandedRenames, setExpandedRenames] = useState<Set<string>>(new Set()); // Track which rename fields are expanded

  // Load Moodle sources and S3 environments on mount
  useEffect(() => {
    loadMoodleSources();
    loadS3Environments();
  }, []);

  // Update selected S3 environment when currentEnvironment changes
  useEffect(() => {
    if (destinationType === 's3' && currentEnvironment && s3Environments.length > 0) {
      // Find all environments that match the currentEnvironment prefix
      const matchingEnvs = s3Environments.filter(env => env.prefix === currentEnvironment);
      // Only auto-select if there's exactly one match (avoid ambiguity with same prefix in different buckets)
      if (matchingEnvs.length === 1) {
        setSelectedS3Environment(matchingEnvs[0].id);
      }
      // If multiple matches, don't auto-select - let user choose
    }
  }, [currentEnvironment, destinationType, s3Environments]);

  // Load categories when Moodle source changes
  useEffect(() => {
    if (destinationType === 'moodle' && selectedMoodleSource) {
      loadMoodleCategories();
    }
  }, [destinationType, selectedMoodleSource]);

  // Load destination content when selection changes
  useEffect(() => {
    // Default to 'courses' if sourceContentType is not yet set
    const contentType = sourceContentType || 'courses';
    console.log('[DestinationPane] Loading content - sourceContentType:', sourceContentType, 'contentType:', contentType, 'destinationType:', destinationType);
    if (destinationType === 'moodle' && selectedMoodleSource) {
      loadMoodleContent();
    } else if (destinationType === 's3' && selectedS3Environment) {
      loadS3Content();
    }
  }, [destinationType, selectedMoodleSource, selectedS3Environment, sourceContentType]);

  // Notify parent of selection changes
  useEffect(() => {
    const instanceId = destinationType === 'moodle' ? selectedMoodleSource : selectedS3Environment;
    if (!instanceId) {
      onSelectionChange(null);
      return;
    }

    // Get the instance name
    const instanceName = destinationType === 'moodle'
      ? moodleSources.find(s => s.id === selectedMoodleSource)?.name
      : s3Environments.find(env => env.id === selectedS3Environment)?.name;

    const selection: DestinationSelection = {
      type: destinationType,
      instanceId,
      instanceName,
      customNames, // Include custom names for duplicate items
    };

    if (destinationType === 'moodle') {
      selection.targetCategoryId = selectedCategoryId;
    } else {
      // Map content type to S3 folder
      const folderMap: Record<ContentType, string> = {
        courses: 'courses',
        competency_frameworks: 'competency_frameworks',
        learning_path_templates: 'learning_path_templates',
        categories: 'categories',
      };
      selection.targetFolder = sourceContentType ? folderMap[sourceContentType] : 'courses';

      // Add environment prefix for S3 destinations
      const env = s3Environments.find(e => e.id === selectedS3Environment);
      if (env) {
        selection.environmentPrefix = env.prefix;
      }
    }

    onSelectionChange(selection);
  }, [destinationType, selectedMoodleSource, selectedS3Environment, selectedCategoryId, sourceContentType, customNames, moodleSources, s3Environments, onSelectionChange]);

  const loadMoodleSources = async () => {
    try {
      const sources = await moodleApi.listSources();
      setMoodleSources(sources);
      if (sources.length > 0 && !selectedMoodleSource) {
        setSelectedMoodleSource(sources[0].id);
      }
    } catch (err) {
      console.error('Failed to load Moodle sources:', err);
    }
  };

  const loadS3Environments = async () => {
    try {
      const environments = await basePrefixApi.getEnvironmentsWithBuckets();
      setS3Environments(environments.map(env => ({
        id: env.environmentId,
        name: env.environmentName,
        prefix: env.prefix,
        bucketName: env.bucketName,
      })));
    } catch (err) {
      console.error('Failed to load S3 environments:', err);
    }
  };

  // Helper function to extract display name from metadata
  const getDisplayName = (file: any, itemContentType: ContentType): string => {
    if (!file.metadata) return file.name || file.fullname;

    // For courses: prefer courseFullname, then courseShortname
    if (itemContentType === 'courses') {
      const fullname = file.metadata['courseFullname'] || file.metadata['coursefullname'];
      if (fullname) return fullname;

      const shortname = file.metadata['courseShortname'] || file.metadata['courseshortname'];
      if (shortname) return shortname;
    }

    // For CMI5 activities: prefer cmi5-activity-name
    if (itemContentType === 'cmi5_activities') {
      const activityName = file.metadata['cmi5-activity-name'];
      if (activityName) return activityName;
    }

    // For other content: use any available name field
    const nameField = file.metadata['name'] || file.metadata['title'] || file.metadata['displayname'];
    if (nameField) return nameField;

    // Fallback to filename or fullname
    return file.name || file.fullname;
  };

  const loadMoodleCategories = async () => {
    if (!selectedMoodleSource) return;

    setLoading(true);
    setError(null);
    try {
      const categories = await moodleApi.listCategories(selectedMoodleSource);
      // Ensure we have an array
      setMoodleCategories(Array.isArray(categories) ? categories : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
      setMoodleCategories([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadMoodleContent = async () => {
    if (!selectedMoodleSource) return;

    // Default to 'courses' if sourceContentType is not yet set
    const contentType = sourceContentType || 'courses';
    
    setLoading(true);
    setError(null);
    try {
      let items: any[] = [];
      switch (contentType) {
        case 'courses':
          items = await moodleApi.listCourses(selectedMoodleSource);
          console.log('[DestinationPane] Loaded courses:', items);
          break;
        case 'competency_frameworks':
          items = await moodleApi.listCompetencyFrameworks(selectedMoodleSource);
          break;
        // Add other types as needed
      }
      setMoodleItems(items);
      onItemsChange?.(items);
    } catch (err) {
      console.error('Failed to load Moodle content:', err);
      setMoodleItems([]);
      onItemsChange?.([]);
    } finally {
      setLoading(false);
    }
  };

  const loadS3Content = async () => {
    if (!selectedS3Environment) return;

    // Look up the environment to get the prefix
    const selectedEnv = s3Environments.find(env => env.id === selectedS3Environment);
    if (!selectedEnv) {
      setError('Selected environment not found');
      return;
    }

    // Default to 'courses' if sourceContentType is not yet set
    const contentType = sourceContentType || 'courses';
    console.log('[DestinationPane] loadS3Content - sourceContentType:', sourceContentType, 'contentType:', contentType);

    setLoading(true);
    setError(null);
    try {
      // Set the environment by ID (handles multiple buckets with same prefix)
      await basePrefixApi.setEnvironmentById(selectedS3Environment);

      const folderMap: Record<ContentType, string> = {
        courses: 'courses',
        competency_frameworks: 'competency_frameworks',
        learning_path_templates: 'learning_path_templates',
        categories: 'categories',
      };
      const folder = folderMap[contentType];
      console.log('[DestinationPane] Loading S3 folder:', folder, 'from environment:', selectedEnv.name, 'prefix:', selectedEnv.prefix);
      const items = await contentApi.getFolderContentsWithVersions(folder);
      console.log('[DestinationPane] Loaded S3 items:', items.length, 'items from folder:', folder);
      setS3Items(items);
      onItemsChange?.(items);
    } catch (err) {
      console.error('Failed to load S3 content:', err);
      setS3Items([]);
      onItemsChange?.([]);
    } finally {
      setLoading(false);
    }
  };

  // Determine if a source item is new or will update destination
  const getItemStatus = (sourceItem: any): 'new' | 'update' | 'duplicate' => {
    const destItems = destinationType === 'moodle' ? moodleItems : s3Items;
    
    if (destItems.length === 0) return 'new';

    // Find matching item in destination
    const matchKey = sourceContentType === 'courses' ? 'shortname' : 'idnumber';
    const destItem = destItems.find(item => {
      if (destinationType === 'moodle') {
        // Match by shortname or idnumber
        return item[matchKey] === sourceItem[matchKey];
      } else {
        // S3 destination: match by shortname from metadata first, then filename
        const srcShortname = sourceItem.metadata?.courseshortname || sourceItem.metadata?.courseShortname || sourceItem[matchKey];
        if (srcShortname) {
          const destShortname = item.metadata?.courseshortname || item.metadata?.courseShortname;
          if (destShortname === srcShortname) return true;
        }
        // Fallback to filename matching
        const srcFilename = sourceItem.key?.split('/').pop() || sourceItem.name;
        const destFilename = item.key?.split('/').pop() || item.name;
        return destFilename === srcFilename;
      }
    });

    if (!destItem) return 'new';

    // Compare timestamps
    // For S3 items, prefer metadata timemodified over lastModified (which is the upload time)
    const sourceTime = sourceItem.metadata?.coursetimemodified 
      ? parseInt(sourceItem.metadata.coursetimemodified, 10) 
      : (sourceItem.timemodified || sourceItem.lastModified);
    const destTime = destItem.metadata?.coursetimemodified 
      ? parseInt(destItem.metadata.coursetimemodified, 10)
      : (destItem.timemodified || destItem.lastModified);

    if (!sourceTime || !destTime) return 'duplicate'; // Can't determine, treat as duplicate

    const sourceMs = typeof sourceTime === 'number' ? sourceTime * 1000 : new Date(sourceTime).getTime();
    const destMs = typeof destTime === 'number' ? destTime * 1000 : new Date(destTime).getTime();

    return sourceMs > destMs ? 'update' : 'duplicate';
  };

  // Get selected source items with their details
  const getSelectedItemsWithStatus = () => {
    return sourceItems
      .filter(item => {
        const itemId = item.id?.toString() || item.key;
        return selectedSourceItems.includes(itemId);
      })
      .map(item => ({
        ...item,
        status: getItemStatus(item),
      }));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
      {/* Compact Selectors Row */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={destinationType}
            label="Type"
            onChange={(e) => setDestinationType(e.target.value as SourceType)}
          >
            <MenuItem value="s3">S3</MenuItem>
            <MenuItem value="moodle">Moodle</MenuItem>
          </Select>
        </FormControl>

        {/* Instance Selector */}
        {destinationType === 'moodle' ? (
          <FormControl size="small" sx={{ flex: 2 }}>
            <InputLabel>Instance</InputLabel>
            <Select
              value={selectedMoodleSource}
              label="Instance"
              onChange={(e) => setSelectedMoodleSource(e.target.value)}
            >
              {moodleSources.map((source) => (
                <MenuItem key={source.id} value={source.id}>
                  {source.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl size="small" sx={{ flex: 2 }}>
            <InputLabel>Environment</InputLabel>
            <Select
              value={selectedS3Environment}
              label="Environment"
              onChange={(e) => setSelectedS3Environment(e.target.value)}
            >
              {s3Environments.map((env) => (
                <MenuItem key={env.id} value={env.id}>
                  {env.name} ({env.bucketName})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Moodle-specific: Category Selector */}
      {destinationType === 'moodle' && sourceContentType === 'courses' && (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Autocomplete
            size="small"
            options={moodleCategories}
            getOptionLabel={(option) => `${option.path} (${option.id})`}
            value={moodleCategories.find(c => c.id === selectedCategoryId) || null}
            onChange={(_, newValue) => {
              if (newValue) {
                setSelectedCategoryId(newValue.id);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Category"
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.path}
                  </Typography>
                </Box>
              </li>
            )}
          />
        )
      )}

      {/* S3-specific: Target Folder Display */}
      {destinationType === 's3' && sourceContentType && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Target: <strong>{sourceContentType}</strong>
        </Alert>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Compact Information Box */}
      <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem' }}>
            Destination
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', fontFamily: 'monospace', lineHeight: 1.6 }}>
            {destinationType === 'moodle' ? (
              <>
                → <strong>{moodleSources.find(s => s.id === selectedMoodleSource)?.name || 'Moodle'}</strong>
                {sourceContentType === 'courses' && moodleCategories.find(c => c.id === selectedCategoryId) && (
                  <>
                    <br />
                    {'  '} / <strong>{moodleCategories.find(c => c.id === selectedCategoryId)?.name || `Category ${selectedCategoryId}`}</strong>
                  </>
                )}
              </>
            ) : (
              <>
                → <strong>{s3Environments.find(env => env.id === selectedS3Environment)?.name || selectedS3Environment}</strong>
                {sourceContentType && (
                  <>
                    <br />
                    {'  '} / <strong>{sourceContentType}</strong>
                  </>
                )}
              </>
            )}
          </Typography>
        </Box>

        {/* Actions Section - Show selected items */}
        {selectedSourceItems.length > 0 && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              Actions ({selectedSourceItems.length} selected)
            </Typography>
            <TableContainer sx={{ maxHeight: 180, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', width: 50 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSelectedItemsWithStatus().map((item: any) => {
                    const displayName = sourceContentType === 'courses'
                      ? (item.fullname || item.name || item.key?.split('/').pop())
                      : (item.shortname || item.name || item.key?.split('/').pop());
                    
                    const itemKey = item.id?.toString() || item.key;
                    const isRenameExpanded = expandedRenames.has(itemKey);

                    return (
                      <React.Fragment key={itemKey}>
                        <TableRow hover>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>
                            {displayName}
                            {item.status === 'duplicate' && isRenameExpanded && customNames[itemKey] && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.25 }}>
                                → {customNames[itemKey]}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {item.status === 'new' && (
                              <Chip label="Will Add" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                            {item.status === 'update' && (
                              <Chip label="Will Update" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                            {item.status === 'duplicate' && (
                              <Chip label="Duplicate" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {item.status === 'duplicate' && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newExpanded = new Set(expandedRenames);
                                  if (isRenameExpanded) {
                                    newExpanded.delete(itemKey);
                                  } else {
                                    newExpanded.add(itemKey);
                                  }
                                  setExpandedRenames(newExpanded);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                        {item.status === 'duplicate' && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 0, borderBottom: isRenameExpanded ? undefined : 0 }}>
                              <Collapse in={isRenameExpanded}>
                                <Box sx={{ pb: 1, pt: 0.5 }}>
                                  <TextField
                                    size="small"
                                    label="Custom name (optional)"
                                    placeholder={displayName}
                                    value={customNames[itemKey] || ''}
                                    onChange={(e) => setCustomNames({ ...customNames, [itemKey]: e.target.value })}
                                    helperText="Enter a new name or leave empty to skip"
                                    fullWidth
                                  />
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Existing Items List */}
        {!loading && (destinationType === 'moodle' ? moodleItems : s3Items).length > 0 && (
          <Box sx={{
            borderTop: selectedSourceItems.length > 0 ? 1 : 0,
            borderColor: 'divider',
            pt: selectedSourceItems.length > 0 ? 1.5 : 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>
                Existing Content ({(destinationType === 'moodle' ? moodleItems : s3Items).length})
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                title="Show/Hide Columns"
              >
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Box>
            <TableContainer sx={{ flex: 1, bgcolor: 'background.paper', borderRadius: 1, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Name</TableCell>
                    {visibleColumns.id && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>ID</TableCell>}
                    {visibleColumns.shortname && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Shortname</TableCell>}
                    {visibleColumns.category && destinationType === 'moodle' && (sourceContentType === 'courses' || !sourceContentType) && (
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Category</TableCell>
                    )}
                    {visibleColumns.size && destinationType === 's3' && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Size</TableCell>}
                    {visibleColumns.date && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Date</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(destinationType === 'moodle' ? moodleItems : s3Items).map((item: any) => {
                    // Default to 'courses' if sourceContentType is not set
                    const contentType = sourceContentType || 'courses';
                    const displayName = destinationType === 'moodle'
                      ? (contentType === 'courses' ? (item.fullname || item.name || `Course ${item.id}`) : (item.name || `Item ${item.id}`))
                      : getDisplayName(item, contentType);

                    // Extract course ID from metadata for CMI5 activities (S3 only)
                    // Check both camelCase (courseId) and lowercase (courseid) for compatibility
                    const courseId = destinationType === 's3' && item.metadata
                      ? (item.metadata.courseId
                          ? parseInt(item.metadata.courseId)
                          : item.metadata.courseid
                            ? parseInt(item.metadata.courseid)
                            : null)
                      : item.id; // For Moodle, use item.id directly

                    // Count visible columns for CMI5 row colspan
                    let colSpan = 1; // name
                    if (visibleColumns.id) colSpan++;
                    if (visibleColumns.shortname) colSpan++;
                    if (visibleColumns.category && destinationType === 'moodle' && contentType === 'courses') colSpan++;
                    if (visibleColumns.size && destinationType === 's3') colSpan++;
                    if (visibleColumns.date) colSpan++;

                    return (
                      <React.Fragment key={item.id || item.key || item.name}>
                        <TableRow hover>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {destinationType === 's3' ? (
                                <Tooltip
                                  title={`${displayName}${item.name && item.name !== displayName ? `\nFile: ${item.name}` : ''}\nKey: ${item.key || ''}`}
                                  placement="top"
                                  arrow
                                >
                                  <span>{displayName}</span>
                                </Tooltip>
                              ) : (
                                <span>{displayName}</span>
                              )}
                              {destinationType === 's3' && item.versionInfo && item.versionInfo.hasVersionHistory && (
                                <Chip
                                  label={`${item.versionInfo.currentVersion} (${item.versionInfo.totalVersions})`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                              {destinationType === 's3' && item.versionInfo && item.versionInfo.currentVersion === 'legacy' && (
                                <Chip label="Legacy" size="small" color="default" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          </TableCell>
                          {visibleColumns.id && <TableCell sx={{ fontSize: '0.75rem' }}>{item.id || '-'}</TableCell>}
                          {visibleColumns.shortname && (
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {destinationType === 'moodle'
                                ? (contentType === 'courses' ? item.shortname : item.idnumber)
                                : (item.metadata?.courseShortname || item.metadata?.courseshortname || '-')}
                            </TableCell>
                          )}
                          {visibleColumns.category && destinationType === 'moodle' && contentType === 'courses' && (
                            <TableCell sx={{ fontSize: '0.75rem' }}>{item.category || '-'}</TableCell>
                          )}
                          {visibleColumns.size && destinationType === 's3' && (
                            <TableCell sx={{ fontSize: '0.75rem' }}>{formatBytes(item.size)}</TableCell>
                          )}
                          {visibleColumns.date && (
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {destinationType === 'moodle'
                                ? new Date(item.timemodified * 1000).toLocaleDateString()
                                : new Date(item.lastModified).toLocaleDateString()}
                            </TableCell>
                          )}
                        </TableRow>

                        {/* CMI5 Activities Row - show for courses in both Moodle and S3 destinations */}
                        {destinationType === 'moodle' && contentType === 'courses' && selectedMoodleSource && courseId && (
                          <CMI5ActivitiesRow
                            sourceId={selectedMoodleSource}
                            sourceType="moodle"
                            courseId={courseId}
                            courseName={item.fullname}
                            colSpan={colSpan}
                          />
                        )}
                        {destinationType === 's3' && contentType === 'courses' && selectedS3Environment && courseId && (
                          <CMI5ActivitiesRow
                            sourceId={selectedS3Environment}
                            sourceType="s3"
                            courseId={courseId}
                            courseName={displayName}
                            colSpan={colSpan}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Column Visibility Menu */}
        <Menu
          anchorEl={columnMenuAnchor}
          open={Boolean(columnMenuAnchor)}
          onClose={() => setColumnMenuAnchor(null)}
        >
          <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, id: !prev.id }))}>
            <Checkbox size="small" checked={visibleColumns.id} />
            <Typography variant="body2">ID</Typography>
          </MenuItem>
          <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, shortname: !prev.shortname }))}>
            <Checkbox size="small" checked={visibleColumns.shortname} />
            <Typography variant="body2">Shortname</Typography>
          </MenuItem>
          <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, category: !prev.category }))}>
            <Checkbox size="small" checked={visibleColumns.category} />
            <Typography variant="body2">Category</Typography>
          </MenuItem>
          <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, size: !prev.size }))}>
            <Checkbox size="small" checked={visibleColumns.size} />
            <Typography variant="body2">Size</Typography>
          </MenuItem>
          <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, date: !prev.date }))}>
            <Checkbox size="small" checked={visibleColumns.date} />
            <Typography variant="body2">Date</Typography>
          </MenuItem>
        </Menu>

        {/* Empty State */}
        {!loading && (destinationType === 'moodle' ? moodleItems : s3Items).length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
            No content in destination
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Utility functions
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
